import { useEffect, useState } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

export type LatLngBoundsTuple = [[number, number], [number, number]];

function toBoundsLiteral(b: LatLngBoundsTuple): google.maps.LatLngBoundsLiteral {
  const [[south, west], [north, east]] = b;
  return { south, west, north, east };
}

/**
 * A raster image pinned to geographic bounds — the session coverage PNG.
 *
 * `google.maps.GroundOverlay` takes its url and bounds at construction only, so
 * this tears down and rebuilds when either changes; opacity is settable and is
 * applied in place.
 */
export function GroundOverlay({
  url,
  bounds,
  opacity = 1,
}: {
  url: string;
  bounds: LatLngBoundsTuple;
  opacity?: number;
}) {
  const map = useMap();
  const maps = useMapsLibrary("maps");
  const [overlay, setOverlay] = useState<google.maps.GroundOverlay | null>(null);
  // Depend on the primitive corners, not the array identity — callers rebuild
  // the tuple on every render and an identity dep would thrash the overlay.
  const [[south, west], [north, east]] = bounds;

  useEffect(() => {
    if (!maps || !map) return;
    const o = new maps.GroundOverlay(url, { south, west, north, east });
    o.setMap(map);
    setOverlay(o);
    return () => {
      o.setMap(null);
      setOverlay(null);
    };
  }, [maps, map, url, south, west, north, east]);

  useEffect(() => {
    overlay?.setOpacity(opacity);
  }, [overlay, opacity]);

  return null;
}

/**
 * Fit the viewport to `bounds`.
 *
 * Both maps show live-updating positions, so re-fitting whenever the bounds
 * move would drag the view out from under anyone trying to read it. Two ways
 * to avoid that:
 *   - `once` — frame it on first load and never again (the session map, where
 *     the user pans and zooms to inspect a path while the scrubber runs).
 *   - `fitKey` — re-fit only when this value changes, ignoring bounds drift in
 *     between (the fleet map, which should re-frame when units appear or the
 *     filter changes, but not on every GPS nudge).
 */
export function FitBounds({
  bounds,
  padding = 40,
  maxZoom,
  once = false,
  fitKey,
}: {
  bounds: LatLngBoundsTuple | null;
  padding?: number;
  maxZoom?: number;
  once?: boolean;
  fitKey?: string;
}) {
  const map = useMap();
  const [fitted, setFitted] = useState(false);
  const corners = bounds
    ? [bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]]
    : null;
  const key = fitKey ?? (corners ? corners.join(",") : null);

  useEffect(() => {
    if (!map || !bounds || !key) return;
    if (once && fitted) return;
    const [[south, west], [north, east]] = bounds;
    // A single point has zero extent; fitBounds on it zooms all the way in, so
    // centre on it at a sensible scale instead.
    if (south === north && west === east) {
      map.setCenter({ lat: south, lng: west });
      map.setZoom(maxZoom ?? 18);
    } else {
      map.fitBounds(toBoundsLiteral(bounds), padding);
      if (maxZoom != null) {
        const z = map.getZoom();
        if (z != null && z > maxZoom) map.setZoom(maxZoom);
      }
    }
    setFitted(true);
    // `bounds` is a fresh array each render; `key` is its value identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key, padding, maxZoom, once, fitted]);

  return null;
}

/** The map's current zoom, re-read on every zoom change. */
export function useMapZoom(fallback = 16): number {
  const map = useMap();
  const [zoom, setZoom] = useState(map?.getZoom() ?? fallback);
  useEffect(() => {
    if (!map) return;
    const sync = () => setZoom(map.getZoom() ?? fallback);
    sync();
    const l = map.addListener("zoom_changed", sync);
    return () => l.remove();
  }, [map, fallback]);
  return zoom;
}

/** The latitude at the centre of the viewport — the reference for converting a
 *  real-world width to pixels, which is latitude-dependent in Mercator. */
export function useMapCenterLat(fallback = 0): number {
  const map = useMap();
  const [lat, setLat] = useState(map?.getCenter()?.lat() ?? fallback);
  useEffect(() => {
    if (!map) return;
    const sync = () => setLat(map.getCenter()?.lat() ?? fallback);
    sync();
    const l = map.addListener("idle", sync);
    return () => l.remove();
  }, [map, fallback]);
  return lat;
}
